import type { PapierDeFiche, TypeDeFiche } from '@nextilt/commun';
import { enFrancais } from '../datesEnMots';
import { REGLAGES } from './maintenant';
import { DIFFICULTE } from './proprietes';

/** Les mots de la fiche d'une bulle, sur l'ordinateur et au téléphone. */

/*
 * La fiche au téléphone (ticket 08 du chantier telephone) :
 * la petite feuille des délais rapides, et « Ses étapes ».
 */
export const POSER_DATE_OU_DELAI = '+ Poser une date ou un délai';
export const UNE_DATE_OU_UN_DELAI = 'Une date ou un délai';
export const EXEMPLE_DE_DELAI = 'Exemple : à la banque, on te dit « réponse sous 10 jours ».';
export const TROIS_JOURS = '3 jours';
export const DIX_JOURS = '10 jours';
export const TROIS_SEMAINES = '3 semaines';
export const DATE_PRECISE = 'Une date précise';
export const ANNULER = 'Annuler';
export const SES_ETAPES = 'Ses étapes';
export const PAS_D_ETAPE_DEDANS = 'Pas d’étape dedans.';
export const AJOUTER_UNE_ETAPE_DEDANS = 'Ajouter une étape dedans';
export const AJOUTER = 'Ajouter';
export const CREUSER = 'Creuser';
export function ouvrirLaFicheDe(titre: string): string {
  return `Ouvrir la fiche de « ${titre} »`;
}

/**
 * La fiche refaite d'après ses maquettes (chantier `revue-design`, ticket 02, `design/fiche.html`
 * et `design/fiche-vide.html`, décisions d'Igor du 2026-10-03, « La fiche » 1 à 6).
 */
export const TACHE = 'Tâche';
/** L'explication d'« Idée », au survol (décision 6 : la phrase quitte la fiche). */
export const IDEE_EXPLIQUEE = 'Une idée reste dans le projet mais ne remonte pas dans Maintenant.';
export const DIFFICULTE_COURT = 'Difficulté';
/** L'explication des étoiles, au survol : ce que dit le bloc « Difficulté pour toi » d'avant. */
export function difficulteExpliquee(charge: number | null): string {
  const base = `${DIFFICULTE} : 1 étoile = facile, 5 étoiles = difficile. Re-clique la même étoile pour la retirer.`;
  return charge == null ? `${base} Pas encore réglée : compte comme 3.` : base;
}
export const REMONTE_DANS_MAINTENANT = 'Remonte dans Maintenant';
export const DUREE = 'Durée';
export const OU = 'Où';
/** Les réglages dépliés de la fiche, les mêmes aux deux fiches : leur nom et leur aide. */
export const CA_ME_PREND = 'Ça me prend';
export const CA_ME_PREND_EXPLIQUE = 'Le temps que TOI tu passes. Ne se confond jamais avec le délai du monde.';
/** Les deux cases de « Ça me prend » (chantier duree-a-la-minute, ticket 03) : leur nom lu, leur unité vue. */
export const HEURES_DE_LA_DUREE = 'Heures';
export const MINUTES_DE_LA_DUREE = 'Minutes';
export const UNITE_DES_HEURES = 'h';
export const UNITE_DES_MINUTES = 'min';
export const IMPORTANCE_EXPLIQUEE =
  'C’est toi qui la règles. Non réglée, elle prend celle de l’étape qui la contient, s’il y en a une.';
export const JE_PEUX_LA_FAIRE = 'Je peux la faire';
export const PLANS_DE_VIE = 'Plans de vie';
export const AUCUN_PLAN_CHOISI = 'aucun plan choisi';
export const CHOISIR_UN_PLAN = 'Choisir un plan';
export const NOTES = 'Notes';
export const ECRIRE_UNE_NOTE = 'Écrire une note…';
export const CONDITIONS = 'Conditions';
export const ELLE_ATTEND = 'Elle attend';
export const ELLE_RETIENT = 'Elle retient';
export const ELLE_EST_DEDANS = 'elle est dedans';
export const LIEN_POSE = 'lien posé';
export const L_ATTEND = 'l’attend';
export function parLaBulle(titre: string): string {
  return `par « ${titre} »`;
}
export const ECHEANCE = 'Échéance';
export const RENDEZ_VOUS = 'Rendez-vous';
export const DELAI_D_ATTENTE = 'Délai d’attente';
export const CONDITION = 'Condition';
export const LIEN_WEB = 'Lien web';
export const ADRESSE = 'Adresse';
export const CONTACT = 'Contact';
/**
 * La fiche du téléphone (chantier `telephone-projets-fiche`, ticket 03, `design/telephone.html`,
 * piste « La fiche ») : les pastilles, ce qu'elles disent en entier, les gestes du bas.
 */
export const J_ATTENDS_UN_RETOUR = 'J’attends un retour';
export const LE_MONDE_L_A_POSEE = 'Le monde l’a posée';
export const C_EST_TOI_QUI_L_AS_POSE = 'C’est toi qui l’as posé';
export const APRES_TON_GESTE = 'Après ton geste';
export const OK = 'OK';
/** « Attend : l'avis d'imposition », « Attend : Le devis et 2 autres ». */
export function attendQuoi(premier: string, autres: number): string {
  if (autres <= 0) return `Attend : ${premier}`;
  return `Attend : ${premier} et ${autres} ${autres > 1 ? 'autres' : 'autre'}`;
}
/** « Le monde met 10 jours ». */
export function leMondeMet(delai: string): string {
  return `Le monde met ${delai}`;
}
/** « Depuis le 3 octobre » : le jour où tu as agi. */
export function depuisLe(jour: string): string {
  return `Depuis le ${enFrancais(jour)}`;
}
/**
 * Le panneau Réglages de la fiche au téléphone (chantier `telephone-projets-fiche`, ticket 04,
 * `design/telephone.html`, « Le panneau Réglages, complet ») : trois groupes, la croix qui
 * enlève, Déplacer et Supprimer à part.
 */
export const LA_TACHE = 'La tâche';
export const QUAND_ET_CONDITIONS = 'Quand et conditions';
export const JOINDRE = 'Joindre';
export const SUPPRIMER = 'Supprimer';
export const OUVRIR_LE_LIEN = 'Ouvrir le lien';
export const EN_COURS_DE_DEVELOPPEMENT = 'en cours de développement';
/** La croix d'un réglage : « Enlever : Durée ». */
export function enleverLeReglage(nom: string): string {
  return `Enlever : ${nom}`;
}
export const SUPPRIMER_LA_BULLE = 'Supprimer la bulle';
export const RENOMMER_LA_BULLE = 'Renommer la bulle';
export const BULLE_PRECEDENTE = 'Bulle précédente';
export const BULLE_SUIVANTE = 'Bulle suivante';
export const FERMER_LA_FICHE = 'Fermer la fiche';

/*
 * Le type et le papier d'une fiche (chantier types-de-fiche, modification 02) : le menu
 * « Type ▾ » en haut de la fiche de l'ordinateur.
 */
export const TYPE_DE_FICHE = 'Type';
export const PAPIER_DE_FICHE = 'Papier';
export const COMME_LE_PROJET = 'Comme le projet';
/** Le bouton du menu : « Type : Classique ». */
export function typeDeLaFiche(type: string): string {
  return `${TYPE_DE_FICHE} : ${type}`;
}
/** « Comme le projet · Classique » : ce que la bulle prend si elle n'a rien choisi. */
export function commeLeProjet(valeur: string): string {
  return `${COMME_LE_PROJET} · ${valeur}`;
}
/** Les quatre types d'une fiche, dans l'ordre du menu. */
export const MOTS_DES_TYPES_DE_FICHE: Record<TypeDeFiche, string> = {
  classique: 'Classique',
  note: 'Note',
  contact: 'Contact',
  plan: 'Plan',
};
/** Les quatre papiers d'une fiche Classique, dans l'ordre du menu (le Post-it est parti). */
export const MOTS_DES_PAPIERS_DE_FICHE: Record<PapierDeFiche, string> = {
  blanche: 'Feuille blanche',
  lignes: 'Lignes',
  carreaux: 'Carreaux',
  bristol: 'Bristol',
};
/**
 * Les deux rangées du type et du papier par défaut des fiches d'un projet (chantier types-de-fiche,
 * modification 06) : dans le panneau Habit d'un projet et dans Paramètres, pour un projet neuf. Le
 * papier ne vaut que pour les fiches Classique : les autres portent celui de leur type
 * (reglages-de-la-fiche, modification 01).
 */
export const TYPE_DE_FICHE_EN_ENTIER = 'Type de fiche';
export const PAPIER_DE_LA_FICHE = 'Papier des fiches Classique';
/** Ce que valent les autres types : au survol du papier par défaut, et sur une fiche qui n'est pas Classique. */
export const PAPIER_FIXE_PAR_LE_TYPE = 'Note, Contact et Plan ont le papier de leur type';
/*
 * Le panneau Réglages de la fiche de l'ordinateur (chantier reglages-de-la-fiche, modification
 * 02) : « ⚙ Réglages » retourne la fiche en panneau, quatre intercalaires, « ‹ La fiche » pour
 * revenir.
 */
export const REGLAGES_DE_LA_FICHE = 'Réglages de la fiche';
export const LA_FICHE = 'La fiche';
/** Les quatre intercalaires, dans l'ordre. */
export const MOTS_DES_INTERCALAIRES = {
  etat: 'État',
  dates: 'Dates',
  contact: 'Contact',
  visuel: 'Visuel',
} as const;
/** Dans Dates : la date au plus tôt, que le serveur calcule. */
export const AU_PLUS_TOT = 'Au plus tôt';
/*
 * Les lignes du panneau (reglages-de-la-fiche, deuxième passe de 02, maquettes `panneau-reglages.html`
 * B et `boutons-des-reglages.html` façon 4) : le nom à gauche, les choix à droite ; vide, un « + … »
 * en pointillé qui ouvre sa saisie sur la ligne.
 */
export const C_EST = 'C’est';
export const UNE_TACHE = 'Une tâche';
export const UNE_IDEE = 'Une idée';
export const REPONSE_DU_MONDE = 'Réponse';
export const PLUS_UNE_ECHEANCE = '+ Une échéance';
export const PLUS_UN_RENDEZ_VOUS = '+ Un rendez-vous';
export const PLUS_UN_DELAI = '+ J’attends une réponse sous…';
export const AU_PLUS_TOT_CALCULE = 'Calculée par Nextilt';
export const CONTACT_DE_LA_LIGNE = 'Contact';
export const LIEU = 'Lieu';
export const LIEN = 'Lien';
export const PLUS_UN_CONTACT = '+ Un contact';
export const PLUS_SON_E_MAIL = '+ Son e-mail';
export const PLUS_UN_LIEU = '+ Une adresse';
export const PLUS_UN_LIEN = '+ Une adresse web';
/** Dans Visuel, sur une fiche qui n'est pas Classique. */
export function papierDuType(type: string): string {
  return `Une fiche ${type} a le papier de son type : seule une Classique choisit le sien`;
}
/*
 * La face de la fiche Classique (chantier reglages-de-la-fiche, modification 03 ; maquette
 * `.rubrique h4`) : elle montre ce qui est réglé, les Réglages le changent.
 */
export const QUAND = 'Quand';
export const LIENS = 'Liens';
/*
 * Le bloc « Liens » de la fiche Plan (chantier liens-entre-bulles, ticket 05, « Les mots » de la
 * spec du chantier) : « Sert à » et « Touche à » sont ceux du bandeau de « Lier »
 * (`MOTS_DES_SORTES_A_LIER`) ; « S'appuie sur », l'autre côté d'un « sert à ».
 */
export const S_APPUIE_SUR = 'S’appuie sur';
/** Un lien caché sur la carte, que la fiche Plan montre quand même. */
export const LIEN_CACHE = 'caché';
export const LE_MONDE_MET = 'Le monde met';
/** Le délai du monde et le jour où tu as agi : « 15 jours, depuis le 8 octobre ». */
export function delaiDepuis(delai: string, jour: string | null): string {
  return jour ? `${delai}, depuis le ${enFrancais(jour)}` : delai;
}
/** La durée d'un rendez-vous, après son jour : « · 30 min ». */
export function dureeDuRendezVous(minutes: number): string {
  return ` · ${minutes} min`;
}
/** Au survol d'une pastille, d'une ligne de Quand ou du contact : où elle mène. */
export function ouvrirLIntercalaire(intercalaire: string): string {
  return `${REGLAGES} : ${intercalaire}`;
}
/*
 * La fiche Contact (chantier types-de-fiche, modification 04 ; maquette `.carte-visite`,
 * `.coord`, `.actions`) : le numéro se copie d'un clic, l'adresse ouvre l'itinéraire, l'e-mail
 * la messagerie. Ni SMS, ni Appeler (décision d'Igor du 2026-10-20).
 */
export const ITINERAIRE = 'Itinéraire';
/** La carte de visite d'un contact, dans la fiche du téléphone (chantier telephone-habit, 04). */
export const APPELER = 'Appeler';
export const SMS = 'SMS';
export const E_MAIL = 'E-mail';
export const SON_E_MAIL = 'Son e-mail';
export const COPIER_LE_NUMERO = 'Copier le numéro';
export const COPIE = 'Copié';
export const CORRIGER = 'Corriger';
/** L'intertitre de la note, sous le contact (maquette `.rubrique h4`). */
export const NOTE_DU_CONTACT = 'Note';
/** Le bouton d'une ligne du contact : « Corriger : le contact ». */
export function corrigerLaPiece(sorte: string): string {
  return `${CORRIGER} : ${sorte}`;
}
/*
 * La fiche Plan (chantier types-de-fiche, modification 05 ; maquette `.bandeau-monde`, `.schema`,
 * `.retient`) : ce qu'elle attend, ce qu'elle libère, ce qu'elle retient et sa date au plus tôt.
 * Jamais « en retard » : ce qui attend le monde n'est pas un retard.
 */
export const ELLE_LIBERE = 'Elle libère';
export const AUTRE_PROJET = 'autre projet';
export const RETIENT = 'Retient';
export const NE_RETIENT_RIEN = 'Ne retient rien';
export const AU_PLUS_TOT_DU_PLAN = 'au plus tôt';
export const PAS_DE_DATE_DU_PLAN = 'pas de date';
/** Au-delà de trois : « + 5 autres », qui déplie. */
export function nAutres(n: number): string {
  return `+ ${n} ${n > 1 ? 'autres' : 'autre'}`;
}
/** Le choix n'a pas pu s'enregistrer : la fiche revient à ce qu'elle était. */
export const FICHE_PAS_ECRITE = 'Le choix n’a pas pu s’enregistrer, réessaie';
export function remonterA(titre: string): string {
  return `Remonter à « ${titre} »`;
}
/** Où l'on est parmi les sœurs : « 3 sur 6 ». */
export function surN(position: number, total: number): string {
  return `${position} sur ${total}`;
}
